import { z } from 'zod';

/** A calendar month; `month` is 1–12 (the database convention, ADR 0006). */
export interface YearMonth {
  readonly year: number;
  readonly month: number;
}

export const yearMonthSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
});

export function yearMonthOf(date: Date = new Date()): YearMonth {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
}

export function addMonths(ym: YearMonth, delta: number): YearMonth {
  const index = ym.year * 12 + (ym.month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export function compareYearMonth(a: YearMonth, b: YearMonth): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

/** Inclusive range; empty when `to` is before `from`. */
export function monthRange(from: YearMonth, to: YearMonth): YearMonth[] {
  const out: YearMonth[] = [];
  for (let cur = from; compareYearMonth(cur, to) <= 0; cur = addMonths(cur, 1)) out.push(cur);
  return out;
}

export function yearMonthKey(ym: YearMonth): string {
  return `${ym.year}-${String(ym.month).padStart(2, '0')}`;
}

/** `YYYY-MM` (an `<input type="month">` value, a query parameter); the inverse of `yearMonthKey`. */
export const yearMonthTextSchema = z
  .string()
  .regex(/^(20\d{2}|2100)-(0[1-9]|1[0-2])$/, { message: 'Nederīgs mēnesis' });

export function parseYearMonth(text: string): YearMonth {
  const [year, month] = yearMonthTextSchema.parse(text).split('-').map(Number) as [number, number];
  return { year, month };
}

/**
 * PMAI turnover comment/flag keys are `"<year>-<JS month index>"` — the month is **0-based**
 * (`turnover.js`: `curYear + '-' + curMonth` with `getMonth()`), so `"2026-1"` is February.
 * Used only by the PMAI import (ADR 0007).
 */
export function parseLegacyMonthKey(key: string): YearMonth {
  const m = /^(\d{4})-(\d{1,2})$/.exec(key);
  if (!m) throw new Error(`Invalid PMAI month key: ${key}`);
  const monthIndex = Number(m[2]);
  if (monthIndex < 0 || monthIndex > 11) throw new Error(`Invalid PMAI month index in key: ${key}`);
  return { year: Number(m[1]), month: monthIndex + 1 };
}
